/** Drag and drop on the model board: a field into a section, or a section into a tab. */
import { useState, type DragEvent } from 'react';
import type { MetadataSchemaDefinition } from '@prism/contracts';
import { dropFieldAt, dropSectionAt, type DropTarget } from './modelDraftOps';

type DragItem = { kind: 'field' | 'section'; id: string };

export function useModelDnd(update: (fn: (d: MetadataSchemaDefinition) => void) => void) {
  const [drag, setDrag] = useState<DragItem | null>(null);
  const [drop, setDrop] = useState<DropTarget | null>(null);
  const finishDrag = () => { setDrag(null); setDrop(null); };
  const performDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (drag && drop) {
      if (drag.kind === 'field') update((d) => dropFieldAt(d, drag.id, drop));
      else update((d) => dropSectionAt(d, drag.id, drop));
    }
    finishDrag();
  };
  /** Mark a drop position while a field or section of the given kind is dragged over. */
  const over = (e: DragEvent, kind: 'field' | 'section', target: (below: boolean) => DropTarget | null) => {
    if (drag?.kind !== kind) return;
    const t = target(e.clientY > e.currentTarget.getBoundingClientRect().top + e.currentTarget.getBoundingClientRect().height / 2);
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    if (JSON.stringify(t) !== JSON.stringify(drop)) setDrop(t);
  };
  return { drag, drop, setDrag, finishDrag, performDrop, over };
}

export type ModelDnd = ReturnType<typeof useModelDnd>;
